/**
 * Variable Type Service
 *
 * This service provides access to the variable type registry for resolving
 * type definitions and their properties. Since types are defined as code
 * constants in libs/shared, this service can import them directly without
 * needing API calls.
 */

import {
	VARIABLE_TYPE_REGISTRY,
	VariableFieldType,
	VariableTypeKey,
	getAllVariableTypeDefinitions,
	getAllVariableTypeKeys,
	getVariableTypeDefinition,
	isValidVariableTypeKey,
	type VariableTypeDefinition,
	type VariableTypeProperty,
	type VariableTypeRegistry,
} from '@supply-mover-2-0/shared';

// Re-export types for convenience
export {
	VariableFieldType,
	VariableTypeKey,
	type VariableTypeDefinition,
	type VariableTypeProperty,
	type VariableTypeRegistry,
};

/**
 * Resolved property information for UI display.
 */
export interface ResolvedProperty {
	/** Property key */
	key: string;
	/** Human-readable label */
	label: string;
	/** The field type */
	type: VariableFieldType;
	/** Is this an array? */
	isArray: boolean;
	/** Is this an object? */
	isObject: boolean;
	/** For arrays: what type are items? */
	arrayType?: VariableTypeKey;
	/** For objects: what type is it? */
	objectType?: VariableTypeKey;
	/** Optional description */
	description?: string;
}

/**
 * Service for working with variable types.
 * Can be used as a singleton or instantiated per component.
 */
export class VariableTypeService {
	/**
	 * Get the type registry.
	 * Since it's a code constant, this is synchronous.
	 */
	getTypeRegistry(): VariableTypeRegistry {
		return VARIABLE_TYPE_REGISTRY;
	}

	/**
	 * Get a type definition by key.
	 */
	getType(typeKey: VariableTypeKey): VariableTypeDefinition {
		return getVariableTypeDefinition(typeKey);
	}

	/**
	 * Check if a type key is valid.
	 */
	isValidType(typeKey: string): typeKey is VariableTypeKey {
		return isValidVariableTypeKey(typeKey);
	}

	/**
	 * Get all available type keys.
	 */
	getAllTypeKeys(): Array<VariableTypeKey> {
		return getAllVariableTypeKeys();
	}

	/**
	 * Get all type definitions.
	 */
	getAllTypes(): Array<VariableTypeDefinition> {
		return getAllVariableTypeDefinitions();
	}

	/**
	 * Get the properties for a type as an array of resolved properties.
	 * This is useful for displaying in UI components.
	 */
	getPropertiesForType(typeKey: VariableTypeKey): Array<ResolvedProperty> {
		const typeDef = this.getType(typeKey);

		if (typeDef == null) {
			return [];
		}

		const entries = Object.entries(typeDef.properties) as Array<
			[string, VariableTypeProperty]
		>;

		return entries.map(
			([key, prop]): ResolvedProperty => ({
				key,
				label: prop.label,
				type: prop.type,
				isArray: prop.type === VariableFieldType.ARRAY,
				isObject: prop.type === VariableFieldType.OBJECT,
				arrayType: prop.arrayType,
				objectType: prop.objectType,
				description: prop.description,
			}),
		);
	}

	/**
	 * Resolve nested type properties recursively.
	 * Useful for building a complete tree of available variables.
	 *
	 * @param typeKey The type to resolve
	 * @param maxDepth Maximum nesting depth (default 3)
	 * @param currentDepth Current depth (internal)
	 * @param visited Set of visited types to prevent circular references
	 */
	resolveNestedProperties(
		typeKey: VariableTypeKey,
		maxDepth: number = 3,
		currentDepth: number = 0,
		visited: Set<VariableTypeKey> = new Set(),
	): Array<ResolvedProperty & { children?: Array<ResolvedProperty> }> {
		if (currentDepth >= maxDepth || visited.has(typeKey)) {
			return this.getPropertiesForType(typeKey);
		}

		visited.add(typeKey);

		const properties = this.getPropertiesForType(typeKey);

		return properties.map((prop) => {
			const result: ResolvedProperty & {
				children?: Array<ResolvedProperty>;
			} = { ...prop };

			// Resolve nested types
			if (prop.isArray && prop.arrayType != null) {
				result.children = this.resolveNestedProperties(
					prop.arrayType,
					maxDepth,
					currentDepth + 1,
					new Set(visited),
				);
			} else if (prop.isObject && prop.objectType != null) {
				result.children = this.resolveNestedProperties(
					prop.objectType,
					maxDepth,
					currentDepth + 1,
					new Set(visited),
				);
			}

			return result;
		});
	}

	/**
	 * Get the type label for display.
	 */
	getTypeLabel(typeKey: VariableTypeKey): string {
		const typeDef = this.getType(typeKey);

		return typeDef?.label ?? typeKey;
	}

	/**
	 * Get a human-readable type description for a property.
	 * E.g., "Array of Line Item" or "Address object"
	 */
	getPropertyTypeDescription(prop: ResolvedProperty): string {
		if (prop.isArray && prop.arrayType != null) {
			const itemLabel = this.getTypeLabel(prop.arrayType);

			return `Array of ${itemLabel}`;
		}

		if (prop.isObject && prop.objectType != null) {
			const objLabel = this.getTypeLabel(prop.objectType);

			return `${objLabel} object`;
		}

		// Primitive types
		switch (prop.type) {
			case VariableFieldType.STRING:
				return 'Text';
			case VariableFieldType.NUMBER:
				return 'Number';
			case VariableFieldType.BOOLEAN:
				return 'Yes/No';
			case VariableFieldType.DATE:
				return 'Date';
			case VariableFieldType.CURRENCY:
				return 'Currency';
			case VariableFieldType.EMAIL:
				return 'Email';
			case VariableFieldType.PHONE:
				return 'Phone';
			default:
				return prop.type;
		}
	}
}

/**
 * Singleton instance for convenience.
 */
export const variableTypeService = new VariableTypeService();
